/**
 * The invoice document assembler: `fetchInvoiceInputs` (all I/O) →
 * `buildInvoiceDocument` (pure shaping) → `assembleInvoiceDocument` (both).
 *
 * The split is what makes the numbers testable: every derivation the screen
 * does today — the counterparty fallback chain, the totals breakdown, the
 * payment-history narrowing — is a pure function of fetched rows, so the
 * golden tests assert on hand-written fixtures with no client at all.
 *
 * Isomorphic: no clock (`now` is injected), no i18n singleton, no Sentry, no
 * browser globals. Every read is org-scoped (`organization_id = orgId`) on top
 * of RLS, and a row the caller may not see is reported as not-found rather
 * than as a denial — the API maps `InvoiceNotFoundError` to a 404.
 */
import { buildDocumentMeta } from "../common.js";
import { fetchOrgContext } from "../orgIdentity.js";
import { filterPaymentTransactions } from "./payments.js";
import { AP_INVOICE_DOCUMENT_SELECT, AP_INVOICE_LINE_ITEM_COLUMNS, AR_INVOICE_DOCUMENT_SELECT, AR_INVOICE_LINE_ITEMS_SELECT, } from "./selects.js";
// ---------------------------------------------------------------------------
// Row shapes (what the selects in ./selects.ts actually return)
// ---------------------------------------------------------------------------
/** PostgREST returns a to-one embed as an object; tolerate an array shape. */
function firstEmbed(embed) {
    const row = Array.isArray(embed) ? embed[0] : embed;
    if (!row || typeof row !== "object")
        return null;
    return row;
}
// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------
/**
 * The invoice does not exist, or is not visible to this client (a different
 * org's id, or an RLS denial). The two are deliberately indistinguishable —
 * telling a caller "exists, but not yours" leaks across the tenant boundary.
 * The API maps this to a 404.
 */
export class InvoiceNotFoundError extends Error {
    orgId;
    invoiceType;
    invoiceId;
    constructor(params) {
        super(`${params.invoiceType === "ar" ? "AR invoice" : "AP bill"} ${params.invoiceId} not found in organization ${params.orgId}`);
        this.name = "InvoiceNotFoundError";
        this.orgId = params.orgId;
        this.invoiceType = params.invoiceType;
        this.invoiceId = params.invoiceId;
    }
}
/**
 * Read everything the document needs, in as few round trips as the data
 * dependencies allow: org context + invoice + line items in parallel, then the
 * two lookups that need the invoice row (the AP vendor identity and, for AP,
 * the project) in parallel.
 *
 * Both invoice reads filter `organization_id` AND `id`, so an id from another
 * org returns no row and surfaces as `InvoiceNotFoundError` — RLS is the
 * boundary, this is the belt.
 */
export async function fetchInvoiceInputs(client, params) {
    const { orgId, invoiceType, invoiceId, inlineLogo } = params;
    // Branch on literal table names rather than computing one: the typed client
    // only accepts literals, and a runtime string degrades the whole file's
    // inference (CLAUDE.md "dynamic table access").
    const invoiceQuery = invoiceType === "ar"
        ? client
            .from("accounts_receivable")
            .select(AR_INVOICE_DOCUMENT_SELECT)
            .eq("organization_id", orgId)
            .eq("id", invoiceId)
            .maybeSingle()
        : client
            .from("accounts_payable")
            .select(AP_INVOICE_DOCUMENT_SELECT)
            .eq("organization_id", orgId)
            .eq("id", invoiceId)
            .maybeSingle();
    // One chain per type, the same as `invoiceQuery`: a single `.select()` over
    // the ternary of the two select strings makes TypeScript parse the UNION of
    // both projections, which tips over TS2590 ("union type that is too complex
    // to represent") once the AR string carries `tax_rate_id`.
    const lineItemsQuery = invoiceType === "ar"
        ? client
            .from("invoice_line_items")
            .select(AR_INVOICE_LINE_ITEMS_SELECT)
            .eq("organization_id", orgId)
            .eq("invoice_type", invoiceType)
            .eq("invoice_id", invoiceId)
            // Deterministic order for the document; `buildInvoiceDocument` sorts
            // by sort_order too (stably), so the id tiebreaker settles duplicates.
            .order("sort_order", { ascending: true })
            .order("id", { ascending: true })
        : client
            .from("invoice_line_items")
            .select(AP_INVOICE_LINE_ITEM_COLUMNS)
            .eq("organization_id", orgId)
            .eq("invoice_type", invoiceType)
            .eq("invoice_id", invoiceId)
            .order("sort_order", { ascending: true })
            .order("id", { ascending: true });
    const [orgContext, invoiceResult, lineItemsResult] = await Promise.all([
        fetchOrgContext(client, orgId, { inlineLogo }),
        invoiceQuery,
        lineItemsQuery,
    ]);
    if (invoiceResult.error)
        throw invoiceResult.error;
    const invoice = invoiceResult.data;
    if (!invoice)
        throw new InvoiceNotFoundError({ orgId, invoiceType, invoiceId });
    if (lineItemsResult.error)
        throw lineItemsResult.error;
    // The counterparty's business id — one column name on both tables (see
    // `InvoiceHeaderRow`). Null for a person or an ad-hoc client name.
    const businessId = invoice.business_id ?? null;
    const projectId = invoiceType === "ap" ? (invoice.project_id ?? null) : null;
    const personId = invoice.person_id ?? null;
    // The control account — the same source the AR/AP grids' per-row currency
    // uses (`ar_invoice_index.ar_account_currency` / `ap_bill_index.ap_account_currency`).
    const controlAccountId = (invoiceType === "ar" ? invoice.metadata?.ar_account_id : invoice.metadata?.ap_account_id) || null;
    const [vendorIdentity, project, counterpartyAddress, controlAccountCurrency] = await Promise.all([
        // The counterparty identity block, looked up by business id (neither
        // select carries the identity columns: AP's `business` embed selects only
        // `name`, and the AR select does not embed `businesses` at all).
        // Org-scoped transitively:
        // the business id comes off a row this org can see, and
        // `business_vendor_identity` has no org column of its own.
        //
        // BOTH directions, deliberately. An EU invoice carrying reverse-charge
        // lines must state the CUSTOMER's VAT number (VAT Directive art. 226(11)),
        // which an AR document could not do while this was AP-only; and printing a
        // B2B customer's registration/VAT identity is standard and harmless on
        // every other invoice. A customer with no identity record renders nothing,
        // exactly as an unidentified vendor does.
        businessId ? fetchVendorIdentity(client, businessId) : Promise.resolve(null),
        projectId ? fetchProject(client, orgId, projectId) : Promise.resolve(null),
        // The address the form showed while this invoice was being written, read
        // back from the same records — neither invoice table stores one. See
        // `fetchCounterpartyAddress`.
        fetchCounterpartyAddress(client, orgId, businessId, personId),
        controlAccountId
            ? fetchAccountCurrency(client, orgId, controlAccountId)
            : Promise.resolve(null),
    ]);
    return {
        invoiceType,
        orgContext,
        invoice,
        lineItems: (lineItemsResult.data ?? []),
        vendorIdentity,
        project,
        counterpartyAddress,
        controlAccountCurrency,
    };
}
/** The first value that actually says something — `''` is not an address. */
function firstNonBlank(...values) {
    for (const value of values) {
        if (value && value.trim() !== "")
            return value;
    }
    return null;
}
/**
 * Primary location first, then oldest — and the first of those that actually
 * carries an address wins.
 *
 * The screen reads `locations[0]` off an UNORDERED PostgREST embed
 * (`useBusinesses`, `usePeople`); stable in practice, guaranteed by nothing. A
 * document that reprints differently on two renders would be worse than one
 * that disagrees with an unordered list, so the order is pinned here. A
 * primary location with a blank `formatted_address` falls through to the next
 * candidate rather than blanking the block.
 */
function addressFromLocations(rows) {
    const sorted = [...rows].sort((a, b) => {
        const primary = Number(b.is_primary === true) - Number(a.is_primary === true);
        if (primary !== 0)
            return primary;
        return (a.created_at ?? "").localeCompare(b.created_at ?? "");
    });
    for (const row of sorted) {
        const embed = firstEmbed(row.gis_locations);
        const address = firstNonBlank(embed?.formatted_address);
        if (address)
            return address;
    }
    return null;
}
/**
 * The counterparty's address, resolved from the customer/vendor record — the
 * SAME ladder the invoice form's Bill-To card shows on screen
 * (`CreateARInvoiceForm` → `useBusinesses`/`usePeople`): the primary linked
 * `gis_locations.formatted_address`, else another linked one, else — for a
 * business — the legacy `businesses.address` column.
 *
 * Resolved, not read off the invoice, because NEITHER TABLE HAS IT:
 * `accounts_receivable.client_address` is a legacy column no write path
 * populates (`create/update_ar_invoice_with_line_items` take a customer name
 * and email, no address) and `accounts_payable` has no address column at all.
 * The result was a document whose Bill To block printed name and email while
 * the form that created it also showed the address. Resolving fixes every
 * invoice already in the database; a stored snapshot, if one is ever written,
 * still wins over this (see `buildCounterparty`).
 *
 * A business wins over a person when a row somehow carries both, matching
 * `resolveCounterpartyName`'s own precedence.
 *
 * The business read is org-scoped EXPLICITLY, not just by RLS: `business_id`'s
 * foreign key proves the business EXISTS, not that it belongs to this org, and
 * a user who belongs to two orgs passes RLS on both — so a cross-org id would
 * print another org's address on this org's invoice. The person arm needs no
 * such filter:
 * `person_id` is a real FK read off the invoice row itself, the same trust the
 * document already extends to the `person` embed it prints the email from.
 */
async function fetchCounterpartyAddress(client, orgId, businessId, personId) {
    if (businessId) {
        const { data, error } = await client
            .from("businesses")
            .select("address, business_locations(is_primary, created_at, gis_locations(formatted_address))")
            .eq("organization_id", orgId)
            .eq("id", businessId)
            .maybeSingle();
        if (error)
            throw error;
        if (!data)
            return null;
        const row = data;
        return firstNonBlank(addressFromLocations(row.business_locations ?? []), row.address);
    }
    if (personId) {
        const { data, error } = await client
            .from("person_locations")
            .select("is_primary, created_at, gis_locations(formatted_address)")
            .eq("person_id", personId);
        if (error)
            throw error;
        return addressFromLocations((data ?? []));
    }
    return null;
}
async function fetchVendorIdentity(client, businessId) {
    const { data, error } = await client
        .from("business_vendor_identity")
        .select("trading_name, registration_number, vat_number, country_code, iban, bic, is_related_party")
        .eq("business_id", businessId)
        .maybeSingle();
    if (error)
        throw error;
    return data ?? null;
}
/**
 * The AP bill's project. A separate read on purpose: widening
 * `AP_INVOICE_DOCUMENT_SELECT` with a project embed would change the wire
 * shape of the AP detail endpoint, whose no-project asymmetry its consumers
 * (and its route test) are built on.
 */
/** A chart-of-accounts row's own currency (null = the org's base currency). */
async function fetchAccountCurrency(client, orgId, accountId) {
    const { data, error } = await client
        .from("chart_of_accounts")
        .select("currency")
        .eq("organization_id", orgId)
        .eq("id", accountId)
        .maybeSingle();
    if (error)
        throw error;
    const row = data;
    const currency = row?.currency?.trim();
    return currency ? currency : null;
}
async function fetchProject(client, orgId, projectId) {
    const { data, error } = await client
        .from("projects")
        .select("id, name, code")
        .eq("organization_id", orgId)
        .eq("id", projectId)
        .maybeSingle();
    if (error)
        throw error;
    const row = data;
    return row ? { id: row.id, name: row.name, code: row.code ?? null } : null;
}
/**
 * The counterparty's display name.
 *
 * AP walks the chain the invoice screen has always shown: the business name,
 * else "first last" when a person is linked, else "Unknown Payee". AR uses
 * `client_name` with "Unknown Client" as its fallback. Both fallbacks were
 * English literals in the pre-cutover preview markup — they stay literals here
 * rather than becoming translation keys, so the document keeps rendering the
 * same strings; localizing them is a template-layer decision.
 */
function resolveCounterpartyName(invoiceType, invoice) {
    if (invoiceType === "ap") {
        const business = firstEmbed(invoice.business);
        if (business?.name)
            return business.name;
        const person = firstEmbed(invoice.person);
        if (person)
            return `${person.first_name} ${person.last_name}`;
        return "Unknown Payee";
    }
    return invoice.client_name || "Unknown Client";
}
function buildCounterparty(inputs) {
    const { invoiceType, invoice, vendorIdentity, project, counterpartyAddress } = inputs;
    const person = firstEmbed(invoice.person);
    const embeddedProject = firstEmbed(invoice.project);
    return {
        name: resolveCounterpartyName(invoiceType, invoice),
        // AP shows the linked person's email, AR the client email — the two
        // branches the `InvoiceDocument` template prints.
        email: (invoiceType === "ap" ? person?.email : invoice.client_email) ?? null,
        // A stored snapshot wins when there is one: an issued invoice keeps the
        // address it was issued to, however the customer has moved since. Nothing
        // writes `client_address` today, so in practice this resolves live until a
        // write path starts snapshotting it.
        address: firstNonBlank(invoiceType === "ar" ? invoice.client_address : null, counterpartyAddress),
        project: embeddedProject
            ? { id: embeddedProject.id, name: embeddedProject.name, code: embeddedProject.code ?? null }
            : project,
        vendorIdentity: vendorIdentity ? mapVendorIdentity(vendorIdentity) : null,
    };
}
function mapVendorIdentity(row) {
    return {
        tradingName: row.trading_name,
        registrationNumber: row.registration_number,
        vatNumber: row.vat_number,
        countryCode: row.country_code,
        iban: row.iban,
        bic: row.bic,
        isRelatedParty: row.is_related_party ?? false,
    };
}
function buildLineItems(rows) {
    // Array.prototype.sort is stable (spec-required), so lines that share a
    // sort_order keep the order the query returned them in (sort_order, id).
    return [...rows]
        .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
        .map((row) => {
        const category = firstEmbed(row.service_category);
        return {
            id: row.id,
            description: row.description ?? "",
            quantity: row.quantity ?? 0,
            unitPrice: row.unit_price ?? 0,
            taxRate: row.tax_rate ?? 0,
            taxRateId: row.tax_rate_id ?? null,
            taxTreatment: row.tax_treatment ?? "standard",
            taxInclusive: row.tax_inclusive ?? false,
            taxAmount: row.tax_amount ?? 0,
            lineTotal: row.line_total ?? 0,
            lineTotalWithTax: row.line_total_with_tax ?? 0,
            exemptionReason: row.exemption_reason ?? null,
            exemptionReasonOther: row.exemption_reason_other ?? null,
            serviceCategory: category?.category_name ? { name: category.category_name } : null,
            sortOrder: row.sort_order ?? 0,
        };
    });
}
/** Collect `name` under `rate` (a rate can carry several names). */
function addRateName(namesByRate, rate, name) {
    const names = namesByRate.get(rate);
    if (names)
        names.add(name);
    else
        namesByRate.set(rate, new Set([name]));
}
/**
 * Rate → tax-rate name, keyed by the line percentage `buildTotals` groups on.
 *
 * Read off the LINES first (AR lines embed their own `tax_rates` row as
 * `tax_rate_ref`): a draft books nothing (20261101120000), so it has no
 * posting journal to name its groups from, and the lines are what the
 * document prints anyway. A rate no line names — a legacy line with no
 * `tax_rate_id`, or an AP bill, whose line select carries no embed — falls
 * back to the invoice's own posting journal (its entries embed
 * `tax_rate:tax_rates(name, rate, direction)`). Either way naming the groups
 * costs no extra query. A rate whose sources disagree on the name — possible
 * in principle, since the same percentage can exist under several
 * names/directions — resolves to null and the template falls back to
 * "<taxName> <rate>%", exactly what the screen prints.
 */
function resolveTaxRateNames(lineItems, posting) {
    const namesByRate = new Map();
    for (const line of lineItems) {
        const taxRate = firstEmbed(line.tax_rate_ref);
        if (typeof line.tax_rate !== "number" || !taxRate?.name)
            continue;
        addRateName(namesByRate, line.tax_rate, taxRate.name);
    }
    const postingNames = new Map();
    for (const entry of posting?.entries ?? []) {
        const taxRate = firstEmbed(entry.tax_rate);
        if (!taxRate || typeof taxRate.rate !== "number" || !taxRate.name)
            continue;
        addRateName(postingNames, taxRate.rate, taxRate.name);
    }
    for (const [rate, names] of postingNames) {
        if (!namesByRate.has(rate))
            namesByRate.set(rate, names);
    }
    const resolved = new Map();
    for (const [rate, names] of namesByRate) {
        resolved.set(rate, names.size === 1 ? [...names][0] : null);
    }
    return resolved;
}
/**
 * The totals block, derived exactly as `InvoiceTotalsBreakdown` derives it:
 *  - subtotal = Σ line_total (net), NOT amount_total − tax;
 *  - a rate group per line with tax_rate > 0 that CARRIES tax, rate DESC;
 *  - amountPaid defaults to 0 and amountRemaining to the total when the
 *    generated columns are null (the component's `?? 0` / `?? amountTotal`);
 *  - a CANCELLED AR invoice owes nothing: amountRemaining is 0.
 *
 * "Carries tax" means `tax_amount !== 0`, not `> 0`. A discount or credit line
 * is stored with a negative `line_total` and a negative `tax_amount` at the
 * same rate as the line it offsets; excluding it printed a VAT figure that did
 * not reconcile — Subtotal (which nets the credit) + VAT (which did not) ≠
 * Total. Zero is excluded on the other side of the same rule: a line tagged
 * with a rate but posting no tax at all (a zero-rated export whose rate field
 * was never cleared) has no tax to report, and inventing a "21% — 0.00" row
 * would misstate the treatment. A group that OPENS and then nets to zero is
 * kept: real tax activity at that rate cancelled out, which is worth saying,
 * and a zero row cannot unbalance the reconciliation.
 */
function buildTotals(inputs, rateNames) {
    const { invoiceType, invoice, lineItems } = inputs;
    const subtotal = lineItems.reduce((sum, item) => sum + (item.line_total || 0), 0);
    const groups = new Map();
    for (const item of lineItems) {
        const rate = item.tax_rate ?? 0;
        const amount = item.tax_amount ?? 0;
        if (rate > 0 && amount !== 0)
            groups.set(rate, (groups.get(rate) ?? 0) + amount);
    }
    const taxByRate = [...groups.entries()]
        .map(([rate, amount]) => ({ rate, name: rateNames.get(rate) ?? null, amount }))
        .sort((a, b) => b.rate - a.rate);
    const amountTotal = invoice.amount_total ?? 0;
    const amountPaid = (invoiceType === "ap" ? invoice.amount_paid : invoice.amount_received) ?? 0;
    // A CANCELLED AR invoice is reversed in full (cancel_ar_invoice), so
    // nothing on it is owed — although `amount_outstanding` (generated:
    // amount_total − amount_received) still reads the full total.
    const cancelled = invoiceType === "ar" && invoice.status === "cancelled";
    const amountRemaining = cancelled
        ? 0
        : ((invoiceType === "ap" ? invoice.amount_remaining : invoice.amount_outstanding) ?? amountTotal);
    return { subtotal, taxByRate, amountTotal, amountPaid, amountRemaining };
}
/**
 * Live payments only. `filterPaymentTransactions` drops the invoice's own
 * posting, VOID journals, and postings superseded by a force-edit — all three
 * carry the invoice id in the embed. Ordered oldest first so the template's
 * "Payment #N" numbering is chronological (the embed's own order is
 * unspecified); id breaks a same-date tie so the output is deterministic.
 */
function buildPayments(invoice) {
    return filterPaymentTransactions(invoice.payment_transactions, invoice.ledger_transaction_id)
        .map((payment) => ({
        id: payment.id,
        transactionNumber: payment.transaction_number ?? "",
        transactionDate: payment.transaction_date ?? "",
        amount: payment.total_amount ?? 0,
        reference: payment.reference ?? null,
    }))
        .sort((a, b) => a.transactionDate.localeCompare(b.transactionDate) || a.id.localeCompare(b.id));
}
/**
 * The statutory statements this invoice must carry as a whole — codes the
 * template turns into sentences (`invoice.taxStatements.<code>`).
 *
 * DERIVED, never read off the invoice. The AR form used to append the
 * reverse-charge sentence to `description` on save and then hydrate its memo
 * field FROM `description`, so every save stacked another copy and removing
 * the reverse-charge line never removed the sentence. A document statement is
 * a fact about the lines, so it is computed from them at assembly and the
 * memo column goes back to holding only what the user typed.
 *
 * Per-line statements are deliberately absent: an exemption's article
 * reference is printed against its own line by `exemptionPhrase`, and
 * repeating it under a heading would say the same thing twice.
 */
function buildTaxStatements(lineItems) {
    const statements = [];
    if (lineItems.some((line) => line.tax_treatment === "reverse_charge")) {
        statements.push("reverse_charge");
    }
    // "VAT not reclaimable" (AP bills only — the CHECK refuses it on AR): the
    // line's VAT sits inside its amount, booked as cost, so the total is said
    // to include VAT that was not reclaimed.
    if (lineItems.some((line) => line.tax_treatment === "non_reclaimable")) {
        statements.push("non_reclaimable");
    }
    return statements;
}
/**
 * The supply date the document prints (AR only): `metadata.supply_date`, a
 * calendar day stored only when it differs from the invoice date. Null when
 * absent, blank or equal to the invoice date — the template prints the line
 * only when it says something the invoice date does not. `invoice_date` may
 * arrive as a bare day or a longer ISO string, so only its day is compared.
 */
function resolveSupplyDate(invoiceType, invoice) {
    if (invoiceType !== "ar")
        return null;
    const supply = invoice.metadata?.supply_date;
    if (typeof supply !== "string" || supply.trim() === "")
        return null;
    return supply === invoice.invoice_date.slice(0, 10) ? null : supply;
}
/**
 * The day a CANCELLED AR invoice was cancelled: its reversal journal's
 * `transaction_date` (the day only). Null on AP, on any other status, and when
 * the reversal is not on the row.
 */
function resolveCancelledOn(invoiceType, invoice) {
    if (invoiceType !== "ar" || invoice.status !== "cancelled")
        return null;
    const day = firstEmbed(invoice.void_transaction)?.transaction_date;
    return typeof day === "string" && day.trim() !== "" ? day.slice(0, 10) : null;
}
/** Pure shaping — no I/O, no clock, no locale lookup beyond `meta`. */
export function buildInvoiceDocument(inputs, options) {
    const { invoiceType, orgContext, invoice } = inputs;
    const posting = firstEmbed(invoice.ledger_transaction);
    return {
        meta: buildDocumentMeta({
            orgId: invoice.organization_id,
            orgTimezone: orgContext.orgTimezone,
            baseCurrency: orgContext.baseCurrency,
            language: options.language,
            now: options.now,
        }),
        org: orgContext.identity,
        // A customer needs our bank details; a vendor's bill must not carry them.
        showBanking: invoiceType === "ar",
        invoiceType,
        invoiceNumber: invoice.invoice_number ?? null,
        invoiceDate: invoice.invoice_date,
        supplyDate: resolveSupplyDate(invoiceType, invoice),
        dueDate: invoice.due_date ?? null,
        cancelledOn: resolveCancelledOn(invoiceType, invoice),
        status: (invoiceType === "ap" ? invoice.payment_status : invoice.status) ?? null,
        approvalStatus: invoiceType === "ap" ? (invoice.approval_status ?? null) : null,
        description: invoice.description ?? null,
        paymentTerms: invoice.payment_terms ?? null,
        selfBilling: invoice.self_billing ?? false,
        additionalVatNotes: invoice.additional_vat_notes ?? null,
        taxStatements: buildTaxStatements(inputs.lineItems),
        currency: inputs.controlAccountCurrency ?? orgContext.baseCurrency,
        counterparty: buildCounterparty(inputs),
        lineItems: buildLineItems(inputs.lineItems),
        totals: buildTotals(inputs, resolveTaxRateNames(inputs.lineItems, posting)),
        payments: buildPayments(invoice),
    };
}
/** Fetch → build. The one call an endpoint or a hook needs. */
export async function assembleInvoiceDocument(client, params) {
    const inputs = await fetchInvoiceInputs(client, {
        orgId: params.orgId,
        invoiceType: params.invoiceType,
        invoiceId: params.invoiceId,
        inlineLogo: params.inlineLogo,
    });
    return buildInvoiceDocument(inputs, { language: params.language, now: params.now });
}
//# sourceMappingURL=assemble.js.map